import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ClientTopbar } from './ClientTopbar';
import { ClientPortalNav } from './ClientPortalNav';
// import { ClientChatBubble } from '../features/ai/ClientChatBubble'; // HIDDEN 2026-06-26 (operator decision; final decision later) — re-enable with the render below
import { ClientFeedbackWidget } from '../features/feedback/ClientFeedbackWidget';
import { ClientOnboardingTour } from '../components/ClientOnboardingTour';

/**
 * Client portal app shell — topbar plus an adaptive navigation sidebar.
 *
 * The shell shipped sidebar-less from the initial commit (900dc5f0), so ten routes
 * were reachable only from five home-page cards and there was no way back from any
 * sub-page but the brand link (#16982, which recorded persistent nav as separate
 * work — this is that work, #18259).
 *
 * The governing references, all binding:
 *   - `.claude/reference/frontend-ui-system.md` § Responsive breakpoints — `md` is
 *     "sidebar off-canvas -> visible", `lg` is "full sidebar".
 *   - `.claude/reference/frontend-ui-system.md` § Mobile navigation pattern —
 *     pinned `w-64` at `lg`, icon-only `w-16` rail between `md` and `lg`, off-canvas
 *     drawer with a backdrop below `md`, closing on tap-outside, Escape and route change.
 *   - `docs/specs/babomrs/phase-05-letter-platform.md` §9.2 — the promoted spec row for
 *     `ClientShell`: "App shell: sidebar with home / signing / documents links; topbar
 *     with user name". The sidebar it names was never built until now.
 */
export function ClientAppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  // Binding: the drawer closes on route change. A drawer that survives a navigation
  // leaves the client looking at the menu they just used instead of the page.
  // Focus is deliberately NOT moved here — it belongs to the new page.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Dismissed rather than navigated (Escape, backdrop, close button): focus returns
  // to the control that opened it, per § Mobile navigation pattern.
  const closeNav = useCallback(() => {
    setNavOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  return (
    <ClientFeedbackWidget>
      <div className="min-h-screen flex flex-col bg-[var(--background)]">
        <ClientTopbar
          navOpen={navOpen}
          onToggleNav={() => setNavOpen((open) => !open)}
          hamburgerRef={hamburgerRef}
        />
        <div className="flex flex-1 min-h-0">
          {navOpen && (
            <div
              className="md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity"
              onClick={closeNav}
              aria-hidden="true"
            />
          )}
          <ClientPortalNav
            open={navOpen}
            onClose={closeNav}
            onNavigate={() => setNavOpen(false)}
          />
          <main className="flex-1 min-w-0 p-4 sm:p-6">
            {/* max-w-4xl, not max-w-2xl (#18259 AC5). The old 672px cap sat BELOW the
                768px `md` breakpoint, so the portal never reached the width at which
                the binding reference says navigation becomes visible. It is a max, so
                the column still shrinks and cannot force horizontal scroll. */}
            <div className="max-w-4xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
        {/* ClientChatBubble HIDDEN 2026-06-26 — operator decision (final decision later).
            Component + backend (/api/v1/ai/chat, Phase-8 tools) left intact;
            re-enable by restoring the import above and this render. */}
        {/* First-run tour (#10803 AC5). Self-managing: shows once, then remembers. */}
        <ClientOnboardingTour />
      </div>
    </ClientFeedbackWidget>
  );
}
